import { Language, languageToLocalazyLanguageMap } from '@faceup/utils'
import { useCallback } from 'react'
import * as yup from 'yup'

type LocalazyCacheData = {
  language: Language
  timestamp: string
  translations: Record<string, string>
}

const LOCALAZY_CACHE_KEY = 'localazy-translations-cache'
const LOCALAZY_API_BASE_URL = 'https://delivery.localazy.com/_a671680765267692845876611746/_e0'
const LOCALAZY_VERSION_URL = `${LOCALAZY_API_BASE_URL}.v2.json`
const LOCALAZY_CDN_HASH = '68428a1b685ab574cd724acef136b0739d1e47fb'

const schema = yup.object({
  language: yup.string().oneOf(Object.values(Language)).required(),
  timestamp: yup.string().default(''),
  translations: yup.object().required(),
})

const initialValue: LocalazyCacheData = {
  language: Language.En_Us,
  timestamp: '',
  translations: {},
}

const getCachedData = (
  onError?: (error: unknown, context: { source: string }) => void
): LocalazyCacheData => {
  try {
    const stored = localStorage.getItem(LOCALAZY_CACHE_KEY)
    if (!stored) {
      return initialValue
    }
    const parsed = JSON.parse(stored)
    schema.validateSync(parsed, { abortEarly: false })
    return parsed
  } catch (error) {
    onError?.(error, { source: 'localazy-cache-read' })
    return initialValue
  }
}

const setCachedData = (data: LocalazyCacheData): void => {
  try {
    schema.validateSync(data, { abortEarly: false })
    localStorage.setItem(LOCALAZY_CACHE_KEY, JSON.stringify(data))
  } catch (error) {
    console.error('Failed to save Localazy cache data:', error)
  }
}

type LocalazyResponseSchema = {
  files: {
    [LOCALAZY_CDN_HASH]: {
      locales: {
        timestamp: string
        uri: string
      }[]
    }
  }
}

type UseFetchMessagesOptions = {
  handleNewMessages: (messages: Record<string, string>) => void
  onError?: (error: unknown, context: { source: string }) => void
  onWarning?: (
    message: string,
    context: { source?: string; extra?: Record<string, unknown> }
  ) => void
}

const isEmptyTranslations = (translations: Record<string, string>): boolean =>
  Object.keys(translations).length === 0

export const useFetchMessages = ({
  handleNewMessages,
  onError,
  onWarning,
}: UseFetchMessagesOptions) => {
  const setCacheData = useCallback((data: LocalazyCacheData) => {
    setCachedData(data)
  }, [])

  const fetchApiTimestamp = async (language: Language): Promise<string> => {
    try {
      const response = await fetch(LOCALAZY_VERSION_URL, { cache: 'no-store' })
      if (!response.ok) {
        throw new Error('Failed to fetch Localazy API timestamp')
      }
      const data = (await response.json()) as LocalazyResponseSchema

      if (!data?.files?.[LOCALAZY_CDN_HASH]?.locales) {
        onWarning?.('Unexpected Localazy version API response structure', {
          extra: { responseKeys: Object.keys(data ?? {}) },
        })
        return ''
      }

      return (
        data.files[LOCALAZY_CDN_HASH].locales.find(file =>
          file.uri.includes(`/${languageToLocalazyLanguageMap[language]}/`)
        )?.timestamp ?? ''
      )
    } catch (error) {
      console.error('Failed to fetch Localazy API timestamp:', error)
      onError?.(error, { source: 'localazy-fetch-timestamp' })
      return ''
    }
  }

  const fetchTranslations = async (language: Language): Promise<Record<string, string>> => {
    try {
      const localazyLanguage = languageToLocalazyLanguageMap[language]
      const url = `${LOCALAZY_API_BASE_URL}/${LOCALAZY_CDN_HASH}/${localazyLanguage}/file.json`
      const response = await fetch(url, { cache: 'no-store' })
      if (!response.ok) {
        throw new Error('Failed to fetch Localazy translations')
      }
      return await response.json()
    } catch (error) {
      console.error('Failed to fetch Localazy translations:', error)
      onError?.(error, { source: 'localazy-fetch-translations' })
      return {}
    }
  }

  const getMessages = async (language: Language): Promise<Record<string, string>> => {
    const currentCacheData = getCachedData(onError)
    const shouldUseCache =
      currentCacheData.timestamp !== '' && currentCacheData.language === language

    if (shouldUseCache) {
      const cachedTranslations = currentCacheData.translations
      const updateInBackground = async () => {
        try {
          const apiTimestamp = await fetchApiTimestamp(language)
          if (apiTimestamp !== currentCacheData.timestamp && apiTimestamp !== '') {
            const newTranslations = await fetchTranslations(language)

            if (isEmptyTranslations(newTranslations)) {
              onWarning?.('Localazy returned empty translations during background update', {
                source: 'localazy-empty-translations',
              })
              return
            }

            const newCacheData: LocalazyCacheData = {
              language,
              timestamp: apiTimestamp,
              translations: newTranslations,
            }
            setCacheData(newCacheData)
            handleNewMessages(newTranslations)
          }
        } catch (error) {
          console.error('Failed to update Localazy cache data:', error)
          onError?.(error, { source: 'localazy-background-update' })
        }
      }
      void updateInBackground()

      return cachedTranslations
    }

    const [apiTimestamp, translations] = await Promise.all([
      fetchApiTimestamp(language),
      fetchTranslations(language),
    ])

    if (isEmptyTranslations(translations)) {
      onWarning?.('Localazy returned empty translations during initial fetch', {
        source: 'localazy-empty-translations',
      })
      return translations
    }

    const newCacheData: LocalazyCacheData = {
      language,
      timestamp: apiTimestamp,
      translations,
    }

    setCacheData(newCacheData)

    return translations
  }

  return { getMessages }
}
